import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchVendors = createAsyncThunk(
  'vendors/fetchVendors',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getVendors(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVendor = createAsyncThunk(
  'vendors/fetchVendor',
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await api.getVendor(vendorId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCities = createAsyncThunk(
  'vendors/fetchCities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getCities();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyVendor = createAsyncThunk(
  'vendors/fetchMyVendor',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getMyVendor();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMyVendor = createAsyncThunk(
  'vendors/updateMyVendor',
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await api.saveMyVendor(vendorData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadVendorImage = createAsyncThunk(
  'vendors/uploadImage',
  async ({ file, meta }, { rejectWithValue }) => {
    try {
      const response = await api.uploadMyVendorImage(file, meta);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  vendors: [],
  currentVendor: null,
  myVendor: null,
  cities: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {
    city: null,
    category: null,
    search: null,
    price_range: null,
    rating: null,
  },
};

const vendorSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentVendor: (state, action) => {
      state.currentVendor = action.payload;
    },
    clearCurrentVendor: (state) => {
      state.currentVendor = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        city: null,
        category: null,
        search: null,
        price_range: null,
        rating: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateMyVendorProfile: (state, action) => {
      state.myVendor = { ...state.myVendor, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Vendors
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.data) {
          state.vendors = data.data;
          state.pagination = {
            current: data.current_page || 1,
            pageSize: data.per_page || 10,
            total: data.total || 0,
          };
        } else {
          state.vendors = Array.isArray(data) ? data : [];
        }
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Single Vendor
      .addCase(fetchVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.currentVendor = action.payload;
      })
      .addCase(fetchVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Cities
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities = action.payload;
      })
      // Fetch My Vendor
      .addCase(fetchMyVendor.fulfilled, (state, action) => {
        state.myVendor = action.payload;
      })
      // Update My Vendor
      .addCase(updateMyVendor.fulfilled, (state, action) => {
        state.myVendor = action.payload;
      })
      // Upload Image
      .addCase(uploadVendorImage.fulfilled, (state, action) => {
        if (state.myVendor) {
          state.myVendor.images = action.payload.images || state.myVendor.images;
        }
      });
  },
});

export const {
  clearError,
  setCurrentVendor,
  clearCurrentVendor,
  setFilters,
  clearFilters,
  setPagination,
  updateMyVendorProfile,
} = vendorSlice.actions;

export default vendorSlice.reducer;
